import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useIssueDetail, useIssues } from './hooks/useIssues';
import { Button, Badge, Card, FeaturePlaceholder, Alert, KPICard, Table, Avatar } from '@/components/ui';
import { IssueFormModal } from './components/IssueFormModal';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  open: 'bg-red-100 text-red-600',
  assigned: 'bg-blue-100 text-blue-600',
  in_progress: 'bg-amber-100 text-amber-600',
  pending_review: 'bg-indigo-100 text-indigo-600',
  resolved: 'bg-emerald-100 text-emerald-600',
  closed: 'bg-slate-900 text-white',
  escalated: 'bg-red-200 text-red-800',
};

const statusLabels: Record<string, string> = {
  open: 'Đang mở',
  assigned: 'Đã giao',
  in_progress: 'Đang xử lý',
  pending_review: 'Chờ duyệt',
  resolved: 'Đã xử lý',
  closed: 'Đã đóng',
  reopened: 'Mở lại',
  escalated: 'Báo cáo cấp trên',
};

const severityColors: Record<string, string> = {
  low: 'bg-slate-100 text-slate-500',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-amber-100 text-amber-600',
  critical: 'bg-red-100 text-red-600',
};

export default function IssueDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';

  const { data: item, isLoading } = useIssueDetail(id);
  const { updateIssue, isUpdating } = useIssues(companyId);

  const [activeTab, setActiveTab] = useState('overview');
  const [showEdit, setShowEdit] = useState(false);

  if (isLoading) return <div className="p-12 text-center text-slate-500 text-sm">Đang tải chi tiết sự cố...</div>;
  if (!item) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy thông tin sự cố</div>;

  const handleStatusChange = async (newStatus: string) => {
    try {
      await updateIssue({ id: id!, updates: { status_code: newStatus as any } });
      toast.success(`Đã cập nhật trạng thái sang ${statusLabels[newStatus]}`);
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'work', label: 'Công việc liên quan' },
    { value: 'media', label: 'Tài liệu / Ảnh' },
    { value: 'history', label: 'Lịch sử' },
  ];

  return (
    <PageDetailLayout
      title={`${item.title}`}
      description={
        <div className="flex items-center gap-3 mt-1">
          <Badge className={`${statusColors[item.status_code] || 'bg-slate-100 text-slate-600'} border-transparent text-[10px] font-bold `}>
            {statusLabels[item.status_code] || item.status_code}
          </Badge>
          <span className="text-sm text-slate-400 font-mono">#{item.issue_code}</span>
          <span className="text-slate-300">|</span>
          <Badge className={`${severityColors[item.severity]} border-transparent text-[10px] font-bold `}>
            {item.severity}
          </Badge>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-calendar-line" />
            {new Date(item.reported_at).toLocaleDateString('vi-VN')}
          </span>
        </div>
      }
      breadcrumbs={[
        { label: 'Quản lý sự cố', path: '/maintenance/issues' },
        { label: item.issue_code }
      ]}
      headerActions={
        <div className="flex gap-2">
          {item.status_code === 'open' && (
            <AuthButton module="maintenance" action="assign" variant="primary" icon="ri-user-add-line" onClick={() => handleStatusChange('assigned')} isLoading={isUpdating}>
              Tiếp nhận & Phân công
            </AuthButton>
          )}
          {item.status_code === 'assigned' && (
            <AuthButton module="maintenance" action="resolve" variant="success" icon="ri-check-line" onClick={() => handleStatusChange('resolved')} isLoading={isUpdating}>
              Đã xử lý xong
            </AuthButton>
          )}
          <AuthButton module="maintenance" action="update" variant="outline" icon="ri-edit-line" onClick={() => setShowEdit(true)}>
            Chỉnh sửa
          </AuthButton>
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {activeTab === 'overview' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPICard title="Thời gian phản hồi" value="15" icon="ri-timer-line" variant="info" suffix="m" />
                <KPICard title="Đã trôi qua" value="2.5" icon="ri-history-line" variant="warning" suffix="h" />
                <KPICard title="Loại sự cố" value={item.issue_type?.toUpperCase() || '---'} icon="ri-error-warning-line" variant="neutral" />
              </div>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <i className="ri-information-line text-os-primary" />
                  Thông tin sự cố
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-12">
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Dự án</span>
                    <span className="text-sm font-bold text-slate-700">{item.project?.name || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Khu vực (Zone)</span>
                    <span className="text-sm font-bold text-os-primary">{item.zone?.zone_name || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Người báo cáo</span>
                    <span className="text-sm font-bold text-slate-700">{item.reported_by_user?.full_name || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Hạn xử lý (SLA)</span>
                    <span className="text-sm font-bold text-red-500">{item.due_date ? new Date(item.due_date).toLocaleString('vi-VN') : 'Không giới hạn'}</span>
                  </div>
                </div>
                <div className="mt-6">
                  <label className="block text-[10px] font-bold text-slate-400  mb-2">Mô tả sự cố</label>
                  <p className="text-sm text-slate-600 bg-slate-50 p-4 rounded-2xl border border-slate-100 leading-relaxed">
                    {item.description || 'Không có mô tả chi tiết.'}
                  </p>
                </div>
              </Card>

              {item.resolution_note && (
                <Card className="p-6 border-emerald-100 bg-emerald-50/30">
                  <h3 className="font-bold text-emerald-900 mb-4 flex items-center gap-2">
                    <i className="ri-checkbox-circle-line text-emerald-600" />
                    Báo cáo xử lý
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-[10px] font-bold text-emerald-600  mb-1">Nguyên nhân gốc rễ</label>
                      <p className="text-sm text-slate-700">{item.root_cause || 'Đang cập nhật...'}</p>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-emerald-600  mb-1">Phương án đã thực hiện</label>
                      <p className="text-sm text-slate-700">{item.resolution_note}</p>
                    </div>
                  </div>
                </Card>
              )}
            </>
          )}

          {(activeTab === 'work' || activeTab === 'media' || activeTab === 'history') && (
            <FeaturePlaceholder
              title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
              description="Dữ liệu đang được liên kết từ các phân hệ Work Order và Logs."
            />
          )}
        </div>

        <div className="space-y-6">
          <Card className="p-6 bg-slate-900 text-white">
            <h4 className="font-bold mb-4 flex items-center gap-2">
              <i className="ri-alarm-warning-line text-os-primary" />
              Theo dõi SLA
            </h4>
            <div className="space-y-4">
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400  font-bold tracking-wider">Trạng thái</span>
                <Badge className={`${statusColors[item.status_code]} border-transparent text-[10px] font-bold `}>
                  {statusLabels[item.status_code]}
                </Badge>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400">Đã trễ (SLA)</span>
                <span className={`font-bold ${item.is_sla_breach ? 'text-red-400' : 'text-emerald-400'}`}>{item.is_sla_breach ? 'CÓ' : 'KHÔNG'}</span>
              </div>
              <div className="pt-4 border-t border-slate-800">
                {item.is_sla_breach ? (
                  <Alert variant="danger" className="bg-red-900/30 border-red-800 text-red-200">
                    Sự cố này đã vượt quá thời gian xử lý cam kết (SLA).
                  </Alert>
                ) : (
                  <Alert variant="success" className="bg-emerald-900/30 border-emerald-800 text-emerald-200">
                    Sự cố đang được kiểm soát trong khung thời gian cho phép.
                  </Alert>
                )}
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <i className="ri-user-settings-line text-slate-400" />
              Người xử lý
            </h4>
            <div className="flex items-center gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-100">
              <Avatar src={item.assignee?.avatar_url} name={item.assignee?.full_name} size="lg" />
              <div>
                <p className="text-sm font-bold text-slate-900">{item.assignee?.full_name || 'Chưa phân công'}</p>
                <p className="text-xs text-slate-500">{item.team?.name || 'Đội kỹ thuật'}</p>
              </div>
            </div>
            {item.status_code === 'open' && (
              <Button variant="outline" className="w-full mt-4" icon="ri-user-add-line">Giao việc ngay</Button>
            )}
          </Card>
        </div>
      </div>

      <IssueFormModal
        isOpen={showEdit}
        item={item}
        companyId={companyId}
        onClose={() => setShowEdit(false)}
      />
    </PageDetailLayout>
  );
}
